/**
 * React Query Hook: Search Objects
 *
 * Searches for metadata items returning only fullName and metadataType
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { SEARCH_OBJECTS_QUERY } from '@/services/metadata_v1/queries';
import {
  SearchObject,
  SearchObjectsResponse,
  SearchObjectsVariables,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Fetch search objects from API
 */
async function fetchSearchObjects(
  variables: SearchObjectsVariables
): Promise<SearchObject[]> {
  const response = await graphqlRequest<SearchObjectsResponse>(
    SEARCH_OBJECTS_QUERY,
    variables
  );
  return response.searchMetadataItems;
}

/**
 * Hook to search objects by name, returning fullName and metadataType
 *
 * Automatically uses organization ID and retrieval ID from selected organization
 *
 * @param searchString - The search query (minimum 2 characters recommended)
 * @param options - Query options
 * @returns React Query result with search object results
 */
export function useSearchObjects(
  searchString: string,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<SearchObject[], Error> {
  const { enabled = true } = options || {};

  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);

  const organizationId = selectedOrg?.id;
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id;

  const shouldSearch = searchString.trim().length >= 1;

  return useQuery({
    queryKey: ['searchObjects', organizationId, retrievalId, searchString],
    queryFn: () =>
      fetchSearchObjects({
        organizationId: organizationId!,
        retrievalId: retrievalId!,
        searchString: searchString.trim(),
      }),
    enabled: enabled && !!organizationId && !!retrievalId && shouldSearch,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}
